import { useEffect, useRef } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { RequireAdmin, RequireAnonymous, RequireAuth } from './auth/guards.tsx';
import { CallProvider } from './calls/CallContext.tsx';
import { CallOverlay } from './calls/CallOverlay.tsx';
import { CallPill } from './calls/CallPill.tsx';
import { ChatProvider, useChat } from './chat/ChatContext.tsx';
import { useAuth } from './auth/AuthContext.tsx';
import { isNative } from './api/origin.ts';
import {
  HEARTBEAT_MS,
  markRestored,
  readLastRestore,
  readStoredRoute,
  restoreTarget,
  writeStoredRoute,
} from './lib/routeRestore.ts';
import { useGallerySync } from './lib/useGallerySync.ts';
import { useNativeBack } from './lib/useNativeBack.ts';
import { useNativeKeyboard } from './lib/nativeKeyboard.ts';
import { AdminScreen } from './screens/AdminScreen.tsx';
import { ChatScreen } from './screens/ChatScreen.tsx';
import { DevPreviewScreen } from './screens/DevPreviewScreen.tsx';
import { GalleryScreen } from './screens/GalleryScreen.tsx';
import { GroupInfoScreen } from './screens/GroupInfoScreen.tsx';
import { LoginScreen } from './screens/LoginScreen.tsx';
import { ForwardScreen } from './screens/ForwardScreen.tsx';
import { ShareScreen } from './screens/ShareScreen.tsx';
import { NewGroupScreen } from './screens/NewGroupScreen.tsx';
import { PasswordScreen } from './screens/PasswordScreen.tsx';
import { PeerInfoScreen } from './screens/PeerInfoScreen.tsx';
import { ProfileScreen } from './screens/ProfileScreen.tsx';
import { BotSettingsScreen } from './screens/BotSettingsScreen.tsx';
import { SettingsScreen } from './screens/SettingsScreen.tsx';
import { ThemeScreen } from './screens/ThemeScreen.tsx';

/**
 * Keeps the chat list, its socket and the loaded threads alive while the user
 * moves between the list and a conversation — on mobile that is a navigation,
 * not just a pane swap.
 *
 * The call provider sits inside it, above the outlet: a call has to survive
 * moving between chats and the settings screen, and its overlay has to be able
 * to cover any of them. It is inside rather than outside because it needs the
 * same socket, and outside the layout there is none.
 */
/**
 * Puts the app on «Отправить в чат» the moment something is shared into it.
 *
 * A component rather than an effect inside ChatProvider, because the provider
 * is what *holds* a shared arrival and this is what *reacts* to one: the
 * redirect needs the router, and the provider deliberately knows nothing about
 * routes — `NotificationRedirect` below is the same split for the same reason.
 *
 * `replace`, so the cross on that screen does not lead back to it.
 */
function ShareRedirect() {
  const { pendingShare } = useChat();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (pendingShare && location.pathname !== '/share') navigate('/share', { replace: true });
  }, [pendingShare, location.pathname, navigate]);
  return null;
}

/**
 * Opens the conversation a tapped notification asked for.
 *
 * The other half of the arrangement above, and it exists because this used to
 * be a `location.assign` inside the provider: reloading the app is an
 * acceptable way to arrive somewhere when the tap is what started it, and a
 * poor one when the app was already open — the socket, the loaded threads and
 * the half-written message in the conversation being left all go with it. A tap
 * on a banner over a chat somebody is typing in is exactly that case.
 *
 * Cleared as it is acted on rather than left to be compared against the
 * location, so that navigating away from a conversation opened this way cannot
 * be undone by the tap that opened it.
 *
 * Not `replace`: a notification is a detour, and the back button belongs on the
 * conversation it interrupted.
 */
function NotificationRedirect() {
  const { pendingNotification, clearPendingNotification } = useChat();
  const navigate = useNavigate();
  useEffect(() => {
    if (!pendingNotification) return;
    navigate(pendingNotification);
    clearPendingNotification();
  }, [pendingNotification, clearPendingNotification, navigate]);
  return null;
}

/**
 * Returns to the conversation a death of the app just tore somebody out of.
 *
 * The router half of lib/routeRestore.ts, which explains the disease: on some
 * phones the OS kills the renderer or the whole process seconds after a
 * resume, the shell recreates or Android relaunches, and either way the page
 * boots at the list — «открыл чат, и он сам закрылся». This keeps the stored
 * route current, on every navigation and on a heartbeat while the app is on
 * screen, and once per page load sends a boot that follows such a death back
 * to the conversation it interrupted.
 *
 * A sibling of the two redirects above rather than an effect in the provider,
 * for their reason: it needs the router. It sits before them in the tree and
 * decides synchronously on mount, so a pending notification — which arrives
 * through an async drain — lands *on top* of the restored conversation and
 * back from it goes somewhere sensible.
 *
 * A push rather than `replace`, deliberately: the entry under it is the list
 * the boot started on, so the hardware back walks out of the restored
 * conversation the same way it walks out of one opened by hand. Replace would
 * leave the restored chat at history index zero, where useNativeBack reads
 * back as «minimize the app».
 */
let restoreDecided = false;

function RouteRestore() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname + location.search;
  const pathRef = useRef(path);
  pathRef.current = path;

  useEffect(() => {
    if (!isNative || restoreDecided) return;
    restoreDecided = true;
    const now = Date.now();
    const target = restoreTarget(readStoredRoute(), readLastRestore(), now);
    if (target === null) return;
    markRestored(now);
    navigate(target);
  }, [navigate]);

  useEffect(() => {
    if (!isNative) return;
    writeStoredRoute(path, Date.now());
  }, [path]);

  // The heartbeat is what tells a death under a person reading — no navigation
  // for minutes — from an app left hours ago: only a route confirmed alive
  // moments before the boot is worth going back to.
  useEffect(() => {
    if (!isNative) return;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      writeStoredRoute(pathRef.current, Date.now());
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []);

  return null;
}

function ChatLayout() {
  return (
    <ChatProvider>
      <CallProvider>
        {/* Inside the provider because that is where a shared arrival is held,
            and above the outlet because what it decides is which screen there
            should be. */}
        <RouteRestore />
        <ShareRedirect />
        <NotificationRedirect />
        <Outlet />
        {/* Rendered last so they stack above every screen without either of
            them having to know a call exists. */}
        <CallPill />
        <CallOverlay />
      </CallProvider>
    </ChatProvider>
  );
}

/**
 * What sits behind the conversation header, whichever kind of conversation it is:
 * a group's roster, or the other person's profile and your shared media.
 *
 * One URL rather than two because it is one place in the navigation — the header
 * is the only way in, and which screen it means is a fact about the chat rather
 * than a choice the caller makes.
 *
 * Nothing is drawn until the list has the chat: guessing during the moment after
 * a cold start would flash the wrong screen, and rendering neither costs a frame.
 * A chat that is missing once the list has arrived is one that was deleted or
 * never existed — the same reading, and the same way out, as GroupInfoScreen's.
 */
function ChatInfoScreen() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { chats } = useChat();
  const chat = chats.find((one) => one.id === chatId) ?? null;

  useEffect(() => {
    if (chats.length > 0 && chat === null) navigate('/', { replace: true });
  }, [chats.length, chat, navigate]);

  if (chat === null) return null;
  return chat.kind === 'group' ? <GroupInfoScreen /> : <PeerInfoScreen />;
}

export function App() {
  useNativeBack();
  useNativeKeyboard();

  // Here rather than on «Общая библиотека», and that is the entire point: the
  // engine has to be told to carry on by an app that was merely opened, not by
  // somebody visiting the screen that reports on it.
  //
  // Above the routes rather than inside ChatLayout, where it was, because that
  // layout is every authenticated screen except one — `/admin` is its own tree,
  // and an admin who left the app on that screen was the one person whose phone
  // quietly stopped backing anything up. There is nothing to gate: the hook
  // no-ops without a user, and consent still lives in the engine's own
  // UserDefaults, so the login screen costs it a discarded effect.
  useGallerySync(useAuth().user?.id);

  return (
    <Routes>
      <Route element={<RequireAnonymous />}>
        <Route path="/login" element={<LoginScreen />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<ChatLayout />}>
          {/* Desktop shows both panes at either URL; below 720px the URL is
              what decides between the list and the conversation. */}
          <Route path="/" element={<ChatScreen />} />
          <Route path="/chat/:chatId" element={<ChatScreen />} />

          {/* Group screens are pushes over the list for the same reason the
              settings ones are: they read the chats the provider already
              holds, and the socket stays up while they are open. */}
          <Route path="/new-group" element={<NewGroupScreen />} />
          <Route path="/chat/:chatId/info" element={<ChatInfoScreen />} />
          {/* «Переслать»: a push over the conversation, for the same reason —
              it reads the chat list the provider already holds, and the message
              being forwarded is named in the path rather than carried in
              history state, so the screen survives a reload. */}
          <Route path="/chat/:chatId/forward/:messageId" element={<ForwardScreen />} />
          {/* Where something shared into the app from outside lands. Nothing in
              the path: what is being sent is bytes the shell copied rather than
              a row anybody can name, so a reload of this URL has nothing to be
              about and the screen sends itself back to the list. */}
          <Route path="/share" element={<ShareScreen />} />
          {/* The assistant's half of the same idea: a push over the list, so the
              socket and the loaded threads survive a trip to it. Its settings
              belong to the reader rather than to the conversation, so the chat
              id in the path only decides where «назад» goes. */}
          <Route path="/chat/:chatId/bot" element={<BotSettingsScreen />} />

          {/* Settings is a push on top of the list, so it stays inside the
              layout: the socket and the loaded threads survive a trip to the
              theme picker, and the back chevron is the browser's back. */}
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/settings/theme" element={<ThemeScreen />} />
          <Route path="/settings/profile" element={<ProfileScreen />} />
          <Route path="/settings/password" element={<PasswordScreen />} />
          {/* Reachable on the web too, where it says the feature is only in the
              app: the row that leads here is hidden off-native, but a route
              that 404s for somebody who typed the URL is worse than a
              sentence. */}
          <Route path="/settings/gallery" element={<GalleryScreen />} />
        </Route>
      </Route>

      {/* Outside ChatLayout: the dashboard needs neither the socket nor the
          loaded threads, and a non-admin is redirected before it renders. The
          server enforces the same rule on /api/admin regardless. */}
      <Route element={<RequireAdmin />}>
        <Route path="/admin" element={<AdminScreen />} />
      </Route>

      {/* A bench, not a screen: no auth, no socket, and `import.meta.env.DEV`
          is a compile-time constant, so the branch and the import behind it
          are gone from a production build. */}
      {import.meta.env.DEV && <Route path="/dev/preview" element={<DevPreviewScreen />} />}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
